/**
 * SmartProblemListEditor — Phase 4
 *
 * ICD-10-CM validated problem list with inline search, add, resolve, reclassify.
 * Fetches codes from /api/terminology/icd10/search (Phase 2).
 * Mutations go through /api/patients/{id}/problems/* (Phase 3).
 *
 * Rules from discipline addendum:
 *   - Debounce search 300ms
 *   - Show validation badge (✓ valid ICD, ⚠ non-specific) next to each code
 *   - Lifecycle history accessible from each row
 *   - No external deps beyond existing project libraries
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Plus, Check, X, ChevronDown, ChevronUp, RefreshCw,
  AlertTriangle, Clock, ShieldCheck, Edit3, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

function SpecificityBadge({ score, isNonspecific }) {
  if (isNonspecific) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[8px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">
        <AlertTriangle className="w-2.5 h-2.5" /> Non-specific
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-[8px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">
      <CheckCircle2 className="w-2.5 h-2.5" /> Validated
    </span>
  );
}

function ProblemRow({ problem, patientId, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [reclassifyReason, setReclassifyReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const statusColor = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    chronic: 'bg-blue-50 text-blue-700 border-blue-200',
    resolved: 'bg-slate-100 text-slate-500 border-slate-200',
    recurrent: 'bg-amber-50 text-amber-600 border-amber-200',
  }[problem.status] || 'bg-slate-50 text-slate-500';

  const handleResolve = async () => {
    setResolving(true);
    try {
      await api.put(`/patients/${patientId}/problems/${problem.id}/resolve`, {});
      toast.success(`${problem.short_label || problem.condition_name}: resolved`);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    } finally { setResolving(false); }
  };

  const handleReclassify = async () => {
    if (!newCode.trim()) { toast.error('Enter a new code'); return; }
    setSubmitting(true);
    try {
      await api.put(`/patients/${patientId}/problems/${problem.id}/reclassify`,
        { new_code: newCode.trim(), reason: reclassifyReason });
      toast.success('Problem reclassified');
      setReclassifyOpen(false);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code or server error');
    } finally { setSubmitting(false); }
  };

  return (
    <div className={`border-b last:border-0 ${problem.status === 'resolved' ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between px-4 py-3 hover:bg-accent/20 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${problem.status === 'resolved' ? 'line-through' : ''}`}>
              {problem.condition_name}
            </span>
            {problem.icd_code && (
              <Badge variant="outline" className="text-[9px] font-mono shrink-0">
                {problem.icd_code}
              </Badge>
            )}
            {problem.icd_code && (
              <SpecificityBadge
                score={problem.specificity_score}
                isNonspecific={problem.is_nonspecific}
              />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge variant="outline" className={`text-[8px] ${statusColor}`}>{problem.status}</Badge>
            {problem.onset_date && (
              <span className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                <Clock className="w-2.5 h-2.5" /> {problem.onset_date}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {problem.status !== 'resolved' && (
            <>
              <Button size="sm" variant="ghost" className="h-6 text-[9px] px-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => setReclassifyOpen(o => !o)} data-testid={`reclassify-btn-${problem.id}`}>
                <Edit3 className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 text-[9px] px-1.5 text-muted-foreground hover:text-emerald-600"
                onClick={handleResolve} disabled={resolving} data-testid={`resolve-btn-${problem.id}`}>
                {resolving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              </Button>
            </>
          )}
          {(problem.lifecycle_history || []).length > 1 && (
            <button onClick={() => setExpanded(e => !e)}
              className="text-muted-foreground hover:text-foreground p-0.5" data-testid={`history-btn-${problem.id}`}>
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>
      {reclassifyOpen && (
        <div className="px-4 pb-3 flex items-center gap-1.5 bg-accent/10 border-b">
          <input type="text" placeholder="New ICD code (e.g. E11.9)"
            value={newCode} onChange={e => setNewCode(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-background w-32 font-mono"
            data-testid={`reclassify-input-${problem.id}`} />
          <input type="text" placeholder="Reason (optional)"
            value={reclassifyReason} onChange={e => setReclassifyReason(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-background flex-1" />
          <Button size="sm" className="h-7 text-[10px] px-2" onClick={handleReclassify} disabled={submitting}>
            {submitting ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Save'}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[10px] px-1.5"
            onClick={() => setReclassifyOpen(false)}><X className="w-3 h-3" /></Button>
        </div>
      )}
      {expanded && (
        <div className="px-4 pb-2 space-y-0.5 bg-muted/10">
          {(problem.lifecycle_history || []).map((h, i) => (
            <p key={i} className="text-[9px] text-muted-foreground">
              {new Date(h.date).toLocaleDateString('en-US', {month:'short',day:'numeric',year:'numeric'})} —
              <span className="font-medium"> {h.action}</span>
              {h.old_code && ` (${h.old_code} → ${h.new_code})`}
              {h.note && `: ${h.note}`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function AddProblemPanel({ patientId, onAdded, onCancel }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [onsetDate, setOnsetDate] = useState('');
  const [adding, setAdding] = useState(false);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get('/terminology/icd10/search', { params: { q, limit: 8 } });
      setResults(res.data.results || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  const handleAdd = async () => {
    if (!selected) { toast.error('Select a code first'); return; }
    setAdding(true);
    try {
      await api.post(`/patients/${patientId}/problems`, {
        code: selected.code_normalized,
        description_override: selected.description,
        onset_date: onsetDate || undefined,
        clinical_status: 'active',
      });
      toast.success(`Added: ${selected.description}`);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add problem');
    } finally { setAdding(false); }
  };

  return (
    <div className="border-t bg-accent/5 p-3 space-y-2" data-testid="add-problem-panel">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        <ShieldCheck className="w-3 h-3 text-primary" /> Add Problem (ICD-10-CM validated)
      </p>
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <input type="text" placeholder='Search: "hypertension", "E11.9", "ankle pain"...'
            value={query} onChange={e => { setQuery(e.target.value); setSelected(null); }}
            className="w-full text-xs border rounded-md px-2.5 py-1.5 bg-background pr-6"
            autoFocus data-testid="problem-search-input" />
          {searching && <RefreshCw className="w-3 h-3 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />}
        </div>
      </div>
      {results.length > 0 && !selected && (
        <div className="border rounded-lg bg-card shadow-sm max-h-44 overflow-y-auto">
          {results.map(r => (
            <button key={r.code_normalized}
              onClick={() => { setSelected(r); setQuery(r.description); setResults([]); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-accent transition-colors border-b last:border-0"
              data-testid={`search-result-${r.code_normalized}`}>
              <Badge variant="outline" className="text-[8px] font-mono shrink-0">{r.code || r.code_normalized}</Badge>
              <span className="flex-1 truncate">{r.description}</span>
              <SpecificityBadge score={r.specificity_score || 0} isNonspecific={r.is_nonspecific || false} />
            </button>
          ))}
        </div>
      )}
      {selected && (
        <div className="flex items-center gap-1.5 p-2 rounded-md bg-primary/5 border border-primary/20">
          <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="text-xs font-medium flex-1">{selected.description}</span>
          <Badge variant="outline" className="text-[8px] font-mono">{selected.code || selected.code_normalized}</Badge>
          <input type="date" value={onsetDate} onChange={e => setOnsetDate(e.target.value)}
            className="text-[10px] border rounded px-1.5 py-0.5 bg-background w-28" title="Onset date (optional)" />
        </div>
      )}
      <div className="flex gap-1.5">
        <Button size="sm" className="text-xs gap-1 h-7" onClick={handleAdd} disabled={!selected || adding}
          data-testid="add-problem-confirm-btn">
          {adding ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          Add to Problem List
        </Button>
        <Button size="sm" variant="ghost" className="text-xs h-7" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export function SmartProblemListEditor({ patientId, problemList: initialList }) {
  const [problemList, setProblemList] = useState(initialList || []);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await api.get(`/patients/${patientId}`);
      setProblemList(res.data.patient?.problem_list || []);
    } catch { /* silent — stale list is fine */ }
    finally { setLoading(false); }
  }, [patientId]);

  const active = problemList.filter(p => p.status !== 'resolved');
  const resolved = problemList.filter(p => p.status === 'resolved');

  return (
    <div className="space-y-3" data-testid="smart-problem-list-editor">
      <Card>
        <CardHeader className="pb-1.5">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" />
            Active Problems
            <Badge variant="outline" className="text-[10px] ml-auto">{active.length}</Badge>
            {!showAdd && (
              <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 ml-1"
                onClick={() => setShowAdd(true)} data-testid="show-add-problem-btn">
                <Plus className="w-2.5 h-2.5" /> Add
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-xs text-muted-foreground px-4 py-4">Loading...</p>
          ) : active.length === 0 ? (
            <p className="text-xs text-muted-foreground px-4 py-4">No active problems</p>
          ) : (
            active.map(p => (
              <ProblemRow key={p.id || p.condition_name} problem={p} patientId={patientId} onUpdated={refresh} />
            ))
          )}
          {showAdd && (
            <AddProblemPanel patientId={patientId}
              onAdded={() => { setShowAdd(false); refresh(); }}
              onCancel={() => setShowAdd(false)} />
          )}
        </CardContent>
      </Card>

      {resolved.length > 0 && (
        <Card className="opacity-60">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground">
              Resolved / Inactive ({resolved.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {resolved.map(p => (
              <ProblemRow key={p.id || p.condition_name} problem={p} patientId={patientId} onUpdated={refresh} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
